import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const BRANDS = [
  {
    n: "01",
    name: "Global Leather Brand",
    category: "Leather Accessories · 4 Continents",
    metric: "4 continents",
    image: "https://images.unsplash.com/photo-1590874103328-eac38a683ce7?auto=format&fm=webp&q=75&w=1200",
    span: "md:col-span-8",
    aspect: "aspect-[16/10]",
  },
  {
    n: "02",
    name: "Label by Pushp",
    category: "Apparel · Instagram-native",
    metric: "Scaled from zero",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fm=webp&q=75&w=700",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
  },
  {
    n: "03",
    name: "Jashn India",
    category: "Ethnic Apparel · D2C",
    metric: "D2C playbook",
    image: "https://images.unsplash.com/photo-1594633313593-bab3825d0caf?auto=format&fm=webp&q=75&w=700",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
  },
  {
    n: "04",
    name: "Jewellery by Jashn",
    category: "Jewellery · Cross-border",
    metric: "Considered-purchase",
    image: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fm=webp&q=75&w=700",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
  },
  {
    n: "05",
    name: "Silai Machine",
    category: "Bespoke Fashion · D2C",
    metric: "Craft-to-D2C",
    image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fm=webp&q=75&w=700",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
  },
  {
    n: "06",
    name: "Roop to She",
    category: "Beauty & Lifestyle",
    metric: "Social-first",
    image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fm=webp&q=75&w=1200",
    span: "md:col-span-8",
    aspect: "aspect-[16/10]",
  },
];

export default function Brands() {
  return (
    <section id="work" className="relative container-x py-24 md:py-40" data-testid="brands-section">
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-20">
        <div className="col-span-12 md:col-span-4">
          <p className="eyebrow">Selected Work</p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-mono text-xs tracking-[0.28em] uppercase text-steel mb-6"
          >
            &mdash; Before fintech, I built the same acquisition systems for consumer brands.
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.96] tracking-tight text-ink"
            data-testid="brands-title"
          >
            Brands I&rsquo;ve incubated and shipped across borders.
          </motion.h2>
          <p className="mt-6 font-sans text-lg text-steel max-w-xl leading-relaxed">
            300K+ customers acquired across categories. Case studies on request.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-4" data-testid="brands-grid">
        {BRANDS.map((b, i) => (
          <motion.article
            key={b.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className={`col-span-12 ${b.span} card-tech overflow-hidden group cursor-default`}
            data-testid={`brand-card-${b.n}`}
          >
            <div className={`relative w-full ${b.aspect} overflow-hidden bg-obsidian`}>
              <img
                src={b.image}
                alt={b.name}
                width="900"
                height="1200"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
            </div>
            <div className="p-6 md:p-8 flex items-start justify-between gap-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-steel mb-2">
                  Case {b.n} &middot; {b.metric}
                </div>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-ink tracking-tight leading-tight">
                  {b.name}
                </h3>
                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-steel">
                  {b.category}
                </div>
              </div>
              <ArrowUpRight
                size={22}
                strokeWidth={1}
                className="text-steel shrink-0 group-hover:text-laser group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              />
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
